/**
 * MCP crypto utilities (spec §70 §5, §4.3)
 *
 * v2: config-connection-token-v2 = base64url(canonical JSON wrapping workspace-secret-v2).
 * No envelope crypto — config-blob uses standard SecureBlob crypto.
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { hkdf as nobleHkdf } from '@noble/hashes/hkdf'
import {
    encodeWorkspaceSecretV2,
    encodeConfigConnectionTokenV2,
    type WorkspaceSecretV2,
} from '@secureblob/sdk/workspaceSecretV2'
import type { McpConnectionEntry } from './types'

// ── V2 token generation (Spec §4.3) ───────────────────────────

/**
 * Derive a stable readerSeed from writerSeed + blobId via HKDF.
 * Same derivation as configBlobService.ts encodeV2Secret (Spec §8.9).
 */
function deriveConfigReaderSeed(writerSeed: string, blobId: string): string {
    const writerSeedBytes = base64UrlDecodeBytes(writerSeed)
    const readerSeedBytes = nobleHkdf(
        nobleSha256,
        writerSeedBytes,
        new TextEncoder().encode(`secureblob:${blobId}`),
        new TextEncoder().encode('config-blob-reader-seed'),
        32,
    )
    return toBase64UrlBytes(readerSeedBytes)
}

function base64UrlDecodeBytes(str: string): Uint8Array {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    const binary = atob(base64)
    return new Uint8Array([...binary].map(c => c.charCodeAt(0)))
}

function toBase64UrlBytes(bytes: Uint8Array): string {
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function getConfigReaderSeed(entry: Pick<McpConnectionEntry, 'readerSeed' | 'writerSeed' | 'configBlobId'>): string {
    return entry.readerSeed ?? deriveConfigReaderSeed(entry.writerSeed, entry.configBlobId)
}

export function buildConfigWorkspaceSecret(entry: Pick<McpConnectionEntry, 'configBlobId' | 'readKey' | 'readerSeed' | 'writerSeed'>): WorkspaceSecretV2 {
    return {
        v: 2,
        type: 'workspace-secret',
        blobId: entry.configBlobId,
        readKey: entry.readKey,
        readerSeed: getConfigReaderSeed(entry),
        writerSeed: entry.writerSeed,
    }
}

/**
 * Generate a config-connection-token-v2 from a connection entry (Spec §4.3).
 * Wraps a full workspace-secret-v2 (including derived readerSeed) for the config blob.
 * This replaces generateBearerToken for all v2 consumers (FUSE, MCP OAuth).
 */
export function generateConfigConnectionToken(
    entry: McpConnectionEntry,
    apiBaseUrl: string,
): string {
    const secret = encodeWorkspaceSecretV2(buildConfigWorkspaceSecret(entry))
    return encodeConfigConnectionTokenV2({
        v: 2,
        type: 'config-connection-token',
        apiBaseUrl,
        secret,
    })
}

// ── Snippet generation ──

/** Generate mcp.json snippet for a connection, tailored to the target client. */
export type McpClient = 'cursor' | 'antigravity' | 'header'
export type OAuthMcpClient = Exclude<McpClient, 'header'>

/**
 * Unified snippet generator. Cursor/Antigravity use OAuth; header mode emits
 * the direct MCP config format with X-SecureBlob-Key.
 */
export function generateMcpSnippet(
    entry: McpConnectionEntry,
    client: McpClient = 'cursor',
    baseUrl = 'https://secureblob.hoglundit.se',
    connectionToken?: string,
): string {
    if (client === 'header') {
        return generateHeaderSnippet(
            entry.configBlobId,
            connectionToken ?? generateConfigConnectionToken(entry, `${baseUrl}/api/cryptoblobstore`),
            baseUrl,
        )
    }
    return generateOAuthSnippet(entry.configBlobId, client, baseUrl)
}

/**
 * OAuth snippet: points client to /mcp/oauth/{configId} endpoint.
 * Client does OAuth discovery → authorize → WebAuthn → token exchange.
 */
export function generateOAuthSnippet(
    configBlobId: string,
    client: OAuthMcpClient = 'cursor',
    baseUrl = 'https://secureblob.hoglundit.se'
): string {
    const serverUrl = `${baseUrl}/mcp/oauth/${configBlobId}`

    const urlKey = client === 'cursor' ? 'url' : 'serverUrl'
    return JSON.stringify({
        mcpServers: {
            secureblob: {
                [urlKey]: serverUrl,
            },
        },
    }, null, 2)
}

export function generateHeaderSnippet(
    configBlobId: string,
    connectionToken: string,
    baseUrl = 'https://secureblob.hoglundit.se',
): string {
    const serverUrl = `${baseUrl}/mcp/oauth/${configBlobId}`
    return JSON.stringify({
        mcpServers: {
            secureblob: {
                url: serverUrl,
                headers: {
                    'X-SecureBlob-Key': connectionToken,
                },
            },
        },
    }, null, 2)
}



export type LinuxArch = 'x86_64' | 'arm64'

/**
 * Convert a human-readable label into a filesystem-safe mount directory name.
 * E.g. "My Team Prod" → "my-team-prod", "Test!!!" → "test"
 */
export function toMountSlug(label: string): string {
    const slug = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    return slug || 'secureblob'
}

/**
 * Generate one-line Linux install command for secureblob-fuse bootstrap.
 * The label is auto-slugified into a filesystem-safe mount path.
 * Uses config-connection-token-v2 (Spec §4.3) for FUSE/SmartRunner consumption.
 */
export function generateLinuxInstallCommand(
    entry: McpConnectionEntry,
    label: string,
    arch: LinuxArch,
    baseUrl = 'https://secureblob.hoglundit.se',
): string {
    const token = generateConfigConnectionToken(entry, `${baseUrl}/api/cryptoblobstore`)
    const escapedToken = shellEscape(token)
    const slug = toMountSlug(label)
    const escapedPath = shellEscape(slug)
    return `curl -fsSL ${baseUrl}/downloads/secureblob/secureblob_linux_${arch}_fuse.sh | env SECUREBLOB_TOKEN=${escapedToken} SECUREBLOB_PATH=${escapedPath} bash`
}

function shellEscape(value: string): string {
    return `'${value.replace(/'/g, `'"'"'`)}'`
}

// ── Key generation ──

/** Generate random bytes as base64url string. */
export function generateRandomKey(bytes = 32): string {
    const arr = crypto.getRandomValues(new Uint8Array(bytes))
    let binary = ''
    for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i])
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Generate a random blobId for a config blob. */
export function generateConfigBlobId(): string {
    return `mcp-cfg-${generateRandomKey(8)}`
}

// ── Workspace secret v2 generation (Spec §4.1) ──────────────────

/**
 * Generate a full read/write workspace-secret-v2 for a new workspace.
 * Returns base64url(canonical_json(WorkspaceSecretV2)).
 * Replaces legacy generateWorkspaceCapability (Spec §1.2: no backward compat).
 */
export function generateWorkspaceSecretV2(blobId: string): string {
    const readKey = generateRandomKey(32)
    const readerSeed = generateRandomKey(32)
    const writerSeed = generateRandomKey(32)
    return encodeWorkspaceSecretV2({
        v: 2,
        type: 'workspace-secret',
        blobId,
        readKey,
        readerSeed,
        writerSeed,
    })
}
